import { useCallback, useState, useMemo } from "react";

import {
  Avatar,
  Button,
  Key,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import {
  Connection,
  getMessageFromNetworkError,
  useQuerySearchMembers,
  useTransferAuthentication,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { useNotification } from "@zapier/utils";
import {
  FilterTypeahead,
  useEmitConnectionChangeOwnerModalEvent,
} from "@zapier/shared";
import styles from "./ConnectionChangeOwnerModal.module.css";
interface Props {
  connection: Connection;
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface ContentProps extends Props {
  onSubmit: (customuserId: string) => void;
}

function ConnectionChangeOwnerContent({
  connection,
  onSubmit,
  setIsOpen,
}: ContentProps) {
  const t = useTranslations("connections-change-owner-modal");
  const { emit } = useEmitConnectionChangeOwnerModalEvent();
  const currentOwnerId = connection?.owner?.id;
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<Key>(currentOwnerId || "");

  const handleClose = () => {
    emit({
      optionSelected: "cancel",
      connectionId: connection?.publicId ?? undefined,
    });
    setIsOpen(false);
  };

  const { members } = useQuerySearchMembers({
    search: searchValue,
    allowTeams: true,
  });

  const items = useMemo(() => {
    const mapped = members.map((member) => ({
      ...member,
      id: member.id,
      name: member.fullName,
    }));
    return mapped;
  }, [members]);

  // callback fired when a key is selected from the dropdown
  const selectKey = (key: Key | null) => {
    setSelectedKey(key || "");
  };

  const searchChange = (search: string) => {
    setSearchValue(search);
  };

  const isValid =
    !!selectedKey && String(selectedKey) !== String(currentOwnerId);

  const submit = () => {
    if (!isValid) {
      return;
    }
    emit({
      optionSelected: "confirm",
      connectionId: connection?.publicId ?? undefined,
    });
    onSubmit(String(selectedKey));
  };

  return (
    <ModalDialogRoot
      isOpen={true}
      onOpenChange={(isOpen) => (isOpen ? setIsOpen(true) : handleClose())}
      className={styles.changeOwnerModal}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            if (!isValid) return;
            return event.key === "Enter" && submit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.title", { connectionTitle: connection?.title || "" })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          {connection?.sharedWithAll && (
            <>
              {t.rich("messages.owner-access-warning", {
                ownerName: connection?.owner?.fullName || "",
                ownerNameText: (chunks) => (
                  <Text fontWeight={600}>{chunks}</Text>
                ),
              })}
            </>
          )}
          <div className={styles.divider} />
          <FilterTypeahead
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the new-owner field when the modal opens
            autoFocus
            isRequired
            items={items}
            label={t("labels.new-owner")}
            menuWidth="440px"
            onSearchChange={searchChange}
            onSelectKey={selectKey}
            placeholder={t("placeholders.select")}
            selectedKey={selectedKey}
            renderItem={(item: any) => {
              return (
                <div className={styles.menuItem}>
                  <Avatar url={item.photoUrl} name={item.fullName} />
                  <Text tag="p">{item.fullName}</Text>
                </div>
              );
            }}
          />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button variant="secondary" type="button" onPress={handleClose}>
            {t("buttons.cancel")}
          </Button>
          <Button isDisabled={!isValid} onPress={submit} type="submit">
            {t("buttons.change-owner")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function ConnectionChangeOwner({
  id,
  isOpen,
  setIsOpen,
  connection,
}: Props) {
  const t = useTranslations("connections-change-owner-modal");
  const { changeOwner } = useTransferAuthentication();
  const { createNotification } = useNotification();
  const { emit } = useEmitConnectionChangeOwnerModalEvent();

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const handleChangeOwner = useCallback(
    async (customuserId: string) => {
      if (!id || !customuserId) {
        return;
      }

      changeOwner(
        { customuserId, id },
        {
          onSuccess: () => {
            emit({
              eventAction: "view",
              optionSelected: "success",
              connectionId: connection?.publicId ?? undefined,
            });
            createNotification({
              message: (
                <Text>
                  {t("notifications.owner-changed-success", {
                    connectionTitle: connection?.title || "",
                  })}
                </Text>
              ),
              type: "success",
            });
          },
          onError: (error: any) => {
            emit({
              eventAction: "view",
              optionSelected: "error",
              connectionId: connection?.publicId ?? undefined,
            });
            createNotification({
              title: t("notifications.owner-changed-error", {
                connectionTitle: connection?.title || "",
              }),
              message: getMessageFromNetworkError(error),
              type: "error",
            });
          },
        },
      );

      setIsOpen(false);
    },
    [connection, createNotification, id, setIsOpen, t, changeOwner, emit],
  );
  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <ConnectionChangeOwnerContent
      connection={connection}
      id={id}
      isOpen={isOpen}
      onSubmit={handleChangeOwner}
      setIsOpen={setIsOpen}
    />
  );
}
